<template>
  <v-card outlined class="run-version-detail-panel" data-testid="run-version-detail-panel">
    <v-card-title class="subtitle-1 py-2">
      Version details
      <v-spacer />
      <span class="caption grey--text text--darken-1">{{ headline }}</span>
    </v-card-title>
    <v-divider />
    <v-card-text class="py-2">
      <v-row dense>
        <v-col cols="12" md="6">
          <div class="overline">Engines executed</div>
          <v-simple-table dense class="run-version-detail-panel__engines">
            <template #default>
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Version</th>
                  <th>Stage</th>
                  <th class="text-right">Time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in engineRows" :key="row.name" :data-testid="`engine-row-${row.name}`">
                  <td>{{ row.name }}</td>
                  <td class="run-version-detail-panel__mono">{{ row.version || '—' }}</td>
                  <td>
                    <span :class="stageClass(row.status)">{{ row.status || '—' }}</span>
                  </td>
                  <td class="text-right">{{ formatMs(row.duration_ms) }}</td>
                </tr>
                <tr v-if="!engineRows.length">
                  <td colspan="4" class="grey--text">No engine information for this run</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-if="skipped.length" class="caption warning--text mt-1">
            Skipped: {{ skipped.join(', ') }}
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="overline">Models &amp; infrastructure</div>
          <v-simple-table dense class="run-version-detail-panel__infra">
            <template #default>
              <tbody>
                <tr v-for="row in infrastructureRows" :key="row.key">
                  <td class="grey--text text--darken-1">{{ row.label }}</td>
                  <td class="run-version-detail-panel__mono">{{ row.value }}</td>
                </tr>
                <tr v-if="!infrastructureRows.length">
                  <td colspan="2" class="grey--text">No resolved models recorded</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-if="fallbacks.length" class="mt-2" data-testid="silent-fallbacks">
            <div class="overline error--text">Silent fallbacks</div>
            <ul class="caption pl-4">
              <li v-for="(item, index) in fallbacks" :key="index">
                <strong>{{ item.name }}</strong>
                <span v-if="item.detail"> — {{ item.detail }}</span>
              </li>
            </ul>
          </div>
        </v-col>

        <v-col cols="12">
          <div class="overline">
            Configuration
            <span v-if="previousRun" class="text-lowercase">· vs {{ previousLabel }}</span>
          </div>
          <template v-if="!previousRun">
            <div class="caption grey--text text--darken-1">
              No previous succeeded run to compare against.
            </div>
          </template>
          <template v-else-if="!configChanges.length">
            <div class="caption success--text" data-testid="config-diff-identical">
              Same configuration as the previous run.
            </div>
          </template>
          <v-simple-table v-else dense class="run-version-detail-panel__diff">
            <template #default>
              <thead>
                <tr>
                  <th>Setting</th>
                  <th>Previous</th>
                  <th>This run</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="change in configChanges" :key="change.path" :data-testid="`config-diff-${change.path}`">
                  <td>
                    <span :title="change.path">{{ change.label }}</span>
                    <v-chip x-small label outlined class="ml-1">{{ change.kind }}</v-chip>
                  </td>
                  <td class="run-version-detail-panel__mono">{{ formatConfigValue(change.before) }}</td>
                  <td class="run-version-detail-panel__mono">{{ formatConfigValue(change.after) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { diffConfigSnapshots, formatConfigValue, snapshotOf } from '@/utils/configDiff';
import { engineBlockOf, runHeadline, runLabel } from '@/utils/runLabels';
import { formatMs, stageDurations } from '@/utils/runSummary';

const INFRASTRUCTURE_LABELS = Object.freeze({
  stt_provider: 'STT provider',
  stt_model: 'STT model',
  llm_model: 'LLM model',
  sagemaker_endpoint: 'SageMaker endpoint',
  mediapipe_version: 'MediaPipe',
  ruleset_source: 'Ruleset source',
  ruleset_version: 'Ruleset version',
});

/**
 * Collapsible detail under the run header (M2 F6): engines executed with timings, the config
 * diff against the previous SUCCEEDED run, resolved models / infrastructure and the silent
 * fallbacks recorded by the worker.
 */
export default {
  name: 'RunVersionDetailPanel',

  props: {
    /** metadata.run block of the run being viewed. */
    run: { type: Object, default: null },
    /** Gestures payload of the run being viewed (carries analysis_config_snapshot). */
    gestures: { type: Object, default: null },
    /** Ledger doc of the previous SUCCEEDED run (config.snapshot included when loaded). */
    previousRun: { type: Object, default: null },
    previousGestures: { type: Object, default: null },
  },

  computed: {
    headline() {
      return runHeadline(this.run);
    },
    engineBlock() {
      return engineBlockOf(this.run);
    },
    engineRows() {
      const engines = this.engineBlock?.engines || {};
      const executed = Array.isArray(this.run?.engines_executed) ? this.run.engines_executed : [];
      const stages = stageDurations(this.run).stages;
      const names = [...new Set([...executed, ...stages.map((s) => s.name)])];
      return names.map((name) => {
        const stage = stages.find((s) => s.name === name) || null;
        return {
          name,
          version: engines[name] || null,
          status: stage ? stage.status : executed.includes(name) ? 'succeeded' : '',
          duration_ms: stage ? stage.duration_ms : null,
        };
      });
    },
    skipped() {
      const list = Array.isArray(this.run?.engines_skipped) ? this.run.engines_skipped : [];
      return list
        .map((item) =>
          item && typeof item === 'object'
            ? `${item.name || item.engine}${item.reason ? ` (${item.reason})` : ''}`
            : String(item)
        )
        .filter(Boolean);
    },
    infrastructureRows() {
      const resolved = this.engineBlock?.resolved || {};
      const rows = Object.keys(resolved)
        .filter((key) => resolved[key] !== null && resolved[key] !== undefined && resolved[key] !== '')
        .map((key) => ({
          key,
          label: INFRASTRUCTURE_LABELS[key] || key,
          value: String(resolved[key]),
        }));
      if (this.engineBlock?.tool_version) {
        rows.unshift({ key: 'tool_version', label: 'Tool version', value: this.engineBlock.tool_version });
      }
      if (this.engineBlock?.build_sha) {
        rows.unshift({ key: 'build_sha', label: 'Build', value: this.engineBlock.build_sha });
      }
      return rows;
    },
    fallbacks() {
      const list = Array.isArray(this.engineBlock?.silent_fallbacks)
        ? this.engineBlock.silent_fallbacks
        : [];
      return list.map((item) =>
        item && typeof item === 'object'
          ? { name: item.name || item.key || 'fallback', detail: item.detail || '' }
          : { name: String(item), detail: '' }
      );
    },
    configChanges() {
      if (!this.previousRun) return [];
      return diffConfigSnapshots(
        snapshotOf(this.previousRun, this.previousGestures),
        snapshotOf(this.run, this.gestures)
      );
    },
    previousLabel() {
      return runLabel(this.previousRun);
    },
  },

  methods: {
    formatMs,
    formatConfigValue,
    stageClass(status) {
      if (status === 'failed') return 'error--text';
      if (status === 'skipped') return 'warning--text';
      return '';
    },
  },
};
</script>

<style scoped>
.run-version-detail-panel__mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.78rem;
}
</style>
